import { Button, ButtonProps } from '@/components/ui/Button';
import { bookmarkUpdateActionData } from '@/lib/helpers/bookmark';
import { cn } from '@/lib/helpers/utils';
import { useNotificationToast } from '@/lib/hooks/useNotificationToast';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { navigate } from 'gatsby';
import omit from 'lodash/omit';
import React, { PropsWithChildren } from 'react';
import { connect } from 'react-redux';
import { toggleApply } from '../../../../app/redux/actions/globalModalAction';
import updateLastAction from '../../../../app/redux/actions/updateLastAction';
import { selectBookmark } from '../../../../app/redux/features/bookmark';
import { addBookmark } from '../../../../app/redux/features/bookmark/addBookmark';
import { useAppDispatch, useAppSelector } from '../../../../app/redux/hooks';
import { ActionType } from '../../../../app/services/authTypes';
import { asyncTrackingEvent, asyncTrackingEventProps } from '../../../../app/utils/tracking';
import { BookmarkAction, BookMarkState } from '../../../../types/bookmark';
import { Opportunity } from '../../../../types/content';
import { ApplyJobInfo } from '../../../../types/opportunity';

interface ApplyToOpportunityProps extends PropsWithChildren<ButtonProps> {
  opportunity: Opportunity;
  isSignedIn: string;
  location: Location;
  duplicateTrackEventName?: string;
}

/**
 * Returns search box
 * @return {JSX}
 */

const ApplyToOpportunity = React.forwardRef<HTMLButtonElement, ApplyToOpportunityProps>(
  (
    {
      opportunity,
      isSignedIn,
      location,
      size = 'md',
      variant = 'default',
      appearance,
      icon,
      className,
      duplicateTrackEventName,
      ...props
    },
    ref
  ) => {
    const dispatch = useAppDispatch();
    const { showNotificationToast } = useNotificationToast();
    const bookmarkBtnId = Date.now().toString();
    const bookmarkInfo = useAppSelector(selectBookmark);
    const bookmarkState: any = bookmarkInfo.bookmarks[opportunity.uuid]?.state;

    let buttonLabel: string = 'Apply';
    const isPreRegistered: boolean = bookmarkState?.label?.toLowerCase() === BookMarkState.PreRegistered;
    const isSaved: boolean = bookmarkState?.label?.toLowerCase() === BookMarkState.Saved;
    const isApplied: boolean = bookmarkState?.label?.toLowerCase() === BookMarkState.Applied;

    if (opportunity.preregister) {
      if (!bookmarkInfo) {
        return;
      }

      buttonLabel = isPreRegistered ? 'Interest registered' : 'Register your interest';
      const trackingEvent: string = 'opportunity_preregister';
      const trackingEventProps: asyncTrackingEventProps = {
        track: trackingEvent,
        trackAbacus: trackingEvent,
        trackId: opportunity.uuid,
        trackText: buttonLabel
      };
      const trackingEventPropsCollection = [trackingEventProps];
      if (duplicateTrackEventName) {
        trackingEventPropsCollection.push({
          ...trackingEventProps,
          track: duplicateTrackEventName,
          trackAbacus: duplicateTrackEventName
        });
      }

      const onClickActions = () => {
        const postState = bookmarkInfo.bookmarks[opportunity.uuid] ? 'PATCH' : 'POST';
        if (isSignedIn && bookmarkInfo) {
          dispatch(addBookmark({ entityUuid: opportunity.uuid, state: { label: BookMarkState.PreRegistered } })).then(
            (response: any) => {
              if (response && response.success !== false) {
                showNotificationToast({
                  type: 'success',
                  message: "Thanks for showing interest. We'll be in touch when this role opens!"
                });
                trackingEventPropsCollection.forEach((eventProp: any) => asyncTrackingEvent(eventProp));
              } else {
                showNotificationToast({
                  type: 'error',
                  message: 'Sorry, something went wrong.'
                });
              }
            }
          );
        } else {
          dispatch(
            updateLastAction(
              location,
              ActionType.BOOKMARK,
              bookmarkUpdateActionData({
                showModal: false,
                job: opportunity,
                bookmarkAction: {
                  id: bookmarkBtnId,
                  contentId: opportunity.uuid,
                  postState: postState,
                  options: { label: BookMarkState.PreRegistered },
                  context: {
                    notificationToast: {
                      content: (
                        <div className="text-sm">
                          Thanks for showing interest. We'll be in touch when this role opens!.
                        </div>
                      ),
                      duplicateContent: (
                        <div className="text-sm">Whoops. Looks like you've already bookmarked this!</div>
                      )
                    }
                  }
                } as BookmarkAction,
                trackingEventProps: trackingEventPropsCollection
              })
            )
          );
          navigate('/login');
        }
      };

      return (
        <Button
          ref={ref}
          {...omit(props, ['dispatch'])}
          className={cn('[&>*]:pointer-events-none', className)}
          appearance={appearance}
          icon={icon}
          size={size}
          variant={variant}
          disabled={isPreRegistered}
          onClick={onClickActions}
          data-track={`${trackingEvent}_click`}
          data-track-abacus={`${trackingEvent}_click`}
          data-track-id={opportunity.uuid}
          data-track-text={buttonLabel}
          data-cy={`button:apply`}
        >
          <ArrowTopRightOnSquareIcon className="w-4" />
          <span>{buttonLabel}</span>
        </Button>
      );
    }

    if (opportunity.applicationUrl) {
      const trackingEvent: string = 'opportunity_apply';
      const trackingEventProps: asyncTrackingEventProps = {
        track: trackingEvent,
        trackAbacus: trackingEvent,
        trackId: opportunity.uuid,
        trackText: buttonLabel,
        trackTarget: opportunity.applicationUrl
      };
      const trackingEventPropsCollection = [trackingEventProps];
      if (duplicateTrackEventName) {
        trackingEventPropsCollection.push({
          ...trackingEventProps,
          track: duplicateTrackEventName,
          trackAbacus: duplicateTrackEventName
        });
      }

      const onClickActions = () => {
        if (isSignedIn) {
          if (!bookmarkState) {
            // Bookmark if not already saved.
            dispatch(addBookmark({ entityUuid: opportunity.uuid, state: { label: BookMarkState.Saved } }));
            dispatch(toggleApply(true, opportunity));
          } else if (!bookmarkState.label || isSaved) {
            dispatch(toggleApply(true, opportunity));
          }
          trackingEventPropsCollection.forEach((eventProp: any) => asyncTrackingEvent(eventProp));
          window.open(opportunity.applicationUrl);
        } else {
          dispatch(
            updateLastAction(location, ActionType.APPLY, {
              showModal: true,
              applyUrl: opportunity.applicationUrl,
              job: opportunity,
              trackingEventProps: trackingEventPropsCollection
            })
          );
          navigate('/login');
        }
      };
      return (
        <Button
          ref={ref}
          {...omit(props, ['dispatch'])}
          className={cn('[&>*]:pointer-events-none', className)}
          appearance={appearance}
          icon={icon}
          size={size}
          variant={variant}
          onClick={onClickActions}
          data-track={`${trackingEvent}_click`}
          data-track-abacus={`${trackingEvent}_click`}
          data-track-id={opportunity.uuid}
          data-track-text={buttonLabel}
          data-track-target={isSignedIn ? btoa(opportunity.applicationUrl) : ''}
          data-cy={`button:apply`}
        >
          <ArrowTopRightOnSquareIcon className="w-4" />
          <span>{buttonLabel}</span>
        </Button>
      );
    }

    if (opportunity.applicationEmail) {
      const path: string = '/apply-by-email';
      buttonLabel = isApplied ? 'Applied!' : 'Apply by email';

      const trackingEvent: string = 'opportunity_apply_email';
      const trackingEventProps: asyncTrackingEventProps = {
        track: trackingEvent,
        trackAbacus: trackingEvent,
        trackId: opportunity.uuid,
        trackText: buttonLabel,
        trackUrl: path
      };
      const trackingEventPropsCollection: any = [];
      if (duplicateTrackEventName) {
        trackingEventPropsCollection.push({
          ...trackingEventProps,
          track: duplicateTrackEventName,
          trackAbacus: duplicateTrackEventName
        });
      }

      // @todo, we should not be using ApplyJobInfo class, change the apply process to use Opportunity class rest of site.
      const job: Partial<ApplyJobInfo> = {
        id: opportunity.uuid,
        title: opportunity.title,
        url: opportunity.path,
        orgId: opportunity.organisation?.uuid ?? '',
        orgName: opportunity.organisation?.title ?? opportunity.sourceOrganisationName,
        orgLogo: opportunity.organisation?.logo as any,
        orgPath: opportunity.organisation?.path ?? ''
      };
      const params = new URLSearchParams(job as any);

      const onClickActions = () => {
        if (isSignedIn) {
          trackingEventPropsCollection.forEach((eventProp: any) => asyncTrackingEvent(eventProp));
          navigate(path + '?' + params.toString(), { state: { job: job } });
        } else {
          dispatch(
            updateLastAction(
              { pathname: path + '?' + params.toString(), search: location.search },
              ActionType.APPLY_EMAIL,
              {
                showModal: false,
                job: opportunity,
                trackingEventProps: trackingEventPropsCollection
              }
            )
          );
          navigate('/login');
        }
      };

      return (
        <Button
          ref={ref}
          {...omit(props, ['dispatch'])}
          className={cn('[&>*]:pointer-events-none', className)}
          appearance={appearance}
          icon={icon}
          size={size}
          variant={variant}
          disabled={isApplied}
          onClick={onClickActions}
          data-track="opportunity_email_click"
          data-track-abacus="opportunity_email_click"
          data-track-id={opportunity.uuid}
          data-track-text={buttonLabel}
          data-track-url={path}
          data-cy={`button:apply`}
        >
          <ArrowTopRightOnSquareIcon className="w-4" />
          <span>{buttonLabel}</span>
        </Button>
      );
    }

    return null;
  }
);

const mapStateToProps = ({ auth }: any) => {
  return { isSignedIn: auth.isSignedIn };
};

export default connect(mapStateToProps, null, null, { forwardRef: true })(ApplyToOpportunity);
